import React, { useMemo, useState } from 'react';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as commonPackage from '@zxcvbn-ts/language-common';
import * as englishPackage from '@zxcvbn-ts/language-en';
import { MAX_PASSWORD_CHARACTERS, passwordPolicyError } from '../utils/passwordPolicy';
import './PasswordFields.css';


zxcvbnOptions.setOptions({
  translations: englishPackage.translations,
  graphs: commonPackage.adjacencyGraphs,
  dictionary: {
    ...commonPackage.dictionary,
    ...englishPackage.dictionary,
  },
});


function strengthPresentation(score) {
  if (score <= 1) return { label: 'Weak', level: 1, className: 'weak' };
  if (score === 2) return { label: 'Fair', level: 2, className: 'fair' };
  return { label: 'Strong', level: 3, className: 'strong' };
}


function PasswordFields({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
  userInputs = [],
  disabled = false,
  idPrefix = 'new-password',
  labelClassName = 'login-label',
  serverError = '',
}) {
  const [showPassword, setShowPassword] = useState(false);
  const localPolicyError = password ? passwordPolicyError(password) : '';
  const blockingError = serverError || localPolicyError;
  const strength = useMemo(() => {
    if (!password) return null;
    const boundedPassword = Array.from(password.normalize('NFC'))
      .slice(0, MAX_PASSWORD_CHARACTERS)
      .join('');
    const result = zxcvbn(
      boundedPassword,
      [...userInputs, 'Sayable'].filter(value => typeof value === 'string' && value.trim()),
    );
    return { result, ...strengthPresentation(result.score) };
  }, [password, userInputs]);

  let guidance = '';
  if (blockingError) {
    guidance = blockingError;
  } else if (strength) {
    guidance = strength.result.feedback.warning
      || strength.result.feedback.suggestions?.[0]
      || (strength.label === 'Strong'
        ? 'Strong password.'
        : 'Add another unrelated word to make this harder to guess.');
  }

  const passwordsMatch = Boolean(confirmPassword) && password === confirmPassword;
  const passwordsDiffer = Boolean(confirmPassword) && password !== confirmPassword;

  return (
    <div className="password-fields">
      <label className={labelClassName} htmlFor={`${idPrefix}-password`}>Create password</label>
      <div className="password-input-shell">
        <input
          id={`${idPrefix}-password`}
          name={`${idPrefix}-password`}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={event => onPasswordChange(event.target.value)}
          autoComplete="new-password"
          required
          disabled={disabled}
          aria-describedby={`${idPrefix}-password-guidance`}
        />
        <button
          type="button"
          className="password-visibility-toggle"
          onClick={() => setShowPassword(current => !current)}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </div>

      {strength ? (
        <div
          className={`password-strength password-strength--${blockingError ? 'invalid' : strength.className}`}
          id={`${idPrefix}-password-guidance`}
          aria-live="polite"
        >
          <div className="password-strength-heading">
            <span>Strength</span>
            <strong>{blockingError ? 'Needs attention' : strength.label}</strong>
          </div>
          <div
            className="password-strength-meter"
            role="meter"
            aria-label="Password strength"
            aria-valuemin="1"
            aria-valuemax="3"
            aria-valuenow={blockingError ? 1 : strength.level}
            aria-valuetext={blockingError ? 'Needs attention' : strength.label}
          >
            {[1, 2, 3].map(level => (
              <span
                key={level}
                className={level <= (blockingError ? 1 : strength.level) ? 'is-active' : ''}
              />
            ))}
          </div>
          <span className="password-strength-guidance">{guidance}</span>
        </div>
      ) : (
        <p className="password-requirements" id={`${idPrefix}-password-guidance`}>
          Use 15–128 characters. Internal spaces are welcome.
        </p>
      )}

      <label className={labelClassName} htmlFor={`${idPrefix}-confirmation`}>Confirm password</label>
      <input
        id={`${idPrefix}-confirmation`}
        name={`${idPrefix}-confirmation`}
        type={showPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={event => onConfirmPasswordChange(event.target.value)}
        autoComplete="new-password"
        required
        disabled={disabled}
        aria-describedby={`${idPrefix}-match-status`}
      />
      <div
        id={`${idPrefix}-match-status`}
        className={`password-match-status${passwordsMatch ? ' is-match' : ''}${passwordsDiffer ? ' is-mismatch' : ''}`}
        aria-live="polite"
      >
        {passwordsMatch ? 'Passwords match.' : passwordsDiffer ? 'Passwords do not match.' : ''}
      </div>
    </div>
  );
}


export default PasswordFields;
